import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { i18n } from '../i18n/i18n';
import { useLanguage } from '../context/LanguageContext';
import { rewardsToHandOver } from '../utils/handOver';
import TasksTab from './admin/TasksTab';
import RoomsTab from './admin/RoomsTab';
import MembersTab from './admin/MembersTab';
import PendingTab from './admin/PendingTab';
import RewardsTab from './admin/RewardsTab';
import ToolsTab from './admin/ToolsTab';

/**
 * Admin panel shell: header + tab pills. Each tab is its own component in ./admin/ with its own
 * state, forms and handlers (split 2026-10-12; this file used to be ~2,700 lines).
 */

type AdminTab = 'tasks' | 'rooms' | 'members' | 'pending' | 'rewards' | 'tools';
const ADMIN_TABS: AdminTab[] = ['tasks', 'rooms', 'members', 'pending', 'rewards', 'tools'];

const TAB_LABEL: Record<AdminTab, string> = {
  tasks: 'admin_panel.tab_tasks',
  rooms: 'admin_panel.tab_rooms',
  members: 'admin_panel.tab_members',
  pending: 'admin_panel.tab_pending',
  rewards: 'admin_panel.tab_rewards',
  tools: 'admin_panel.tab_tools',
};

const AdminPanel: React.FC = () => {
  const { current_user, current_family } = useAppContext() as any;
  const navigate = useNavigate();
  const { current_language } = useLanguage();
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<AdminTab>(
    ADMIN_TABS.includes(requestedTab as AdminTab) ? (requestedTab as AdminTab) : 'tasks',
  );
  // React to ?tab= changing while already mounted (e.g. the Set up shortcuts on the parent's Home).
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (ADMIN_TABS.includes(tab as AdminTab)) setActiveTab(tab as AdminTab);
  }, [searchParams]);

  if (!current_family || !current_user || !(current_user as any).is_admin) {
    return <div className="text-center text-red-600 dark:text-red-400 text-lg font-semibold py-8">{i18n.t('library.access_denied')}</div>;
  }

  const counts: Partial<Record<AdminTab, number>> = {
    pending: current_family.tasks.filter((t: any) => t.is_completed && !t.verified).length,
    rewards: rewardsToHandOver(current_family.rewards as any[]).length,
  };

  return (
    <div className="w-full max-w-[1180px] mx-auto px-4 md:px-8 py-4 md:py-7 text-kid-text dark:text-kid-text-dark" lang={current_language}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <button
          type="button"
          onClick={() => navigate('/home')}
          aria-label={i18n.t('settings.back')}
          className="w-12 h-12 rounded-full bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border dark:border-kid-border-dark text-2xl font-bold flex items-center justify-center shrink-0"
        >
          <span aria-hidden>←</span>
        </button>
        <h1 className="font-kid-heading font-extrabold text-3xl md:text-4xl" data-lang={current_language}>{i18n.t('admin_panel.header_title')}</h1>
      </div>
      <p className="text-base md:text-lg mb-5">{i18n.t('admin_panel.header_subtitle')}</p>

      {/* Tabs: pills that scroll sideways on narrow screens */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-1" role="tablist">
        {ADMIN_TABS.map((tab) => {
          const count = counts[tab] || 0;
          return (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={activeTab === tab}
              onClick={() => setActiveTab(tab)}
              className={`min-h-[48px] px-5 rounded-full border-2 font-bold text-base whitespace-nowrap inline-flex items-center gap-2 ${
                activeTab === tab
                  ? 'bg-kid-green-bg dark:bg-green-900/40 border-kid-green text-kid-green-shadow dark:text-green-200'
                  : 'bg-kid-card dark:bg-kid-card-dark border-kid-border dark:border-kid-border-dark'
              }`}
            >
              {i18n.t(TAB_LABEL[tab])}
              {count > 0 && (
                <span className="min-w-[24px] h-6 px-1.5 rounded-full bg-kid-orange text-white text-sm flex items-center justify-center">{count}</span>
              )}
            </button>
          );
        })}
      </div>

      <div>
        {activeTab === 'tasks' && <TasksTab />}
        {activeTab === 'rooms' && <RoomsTab />}
        {activeTab === 'members' && <MembersTab />}
        {activeTab === 'pending' && <PendingTab />}
        {activeTab === 'rewards' && <RewardsTab />}
        {activeTab === 'tools' && <ToolsTab />}
      </div>
    </div>
  );
};

export default AdminPanel;
